// Auth diagnostics — decodes the local auth state without hitting the
// network so callers can tell at a glance whether the SDK Mongo token
// (`symbols_access_token`) is present and unexpired. The federated
// governance JWT this file used to inspect was retired with the
// governance Supabase project — the SDK token is the only session.
//
// Use:
//   import { diagnoseAuth } from '<shared>/functions/diagnoseAuth.js'
//   console.table(diagnoseAuth())
// Or call `window.__auth.diagnose()` in the browser console (wired by
// bootShell at boot).

/**
 * Returns a structured snapshot of the local auth state. No network calls.
 *
 * Shape:
 *   {
 *     sdk: { present, expiresInSec, expired },
 *     diagnosis: 'all_missing' | 'sdk_only',
 *     suggestedAction: string
 *   }
 */
export const diagnoseAuth = () => {
  // FA204 / frank trap — inlined (self-contained local) instead of the bare
  // module-scope reads; this is the export's own copy so it stays correct
  // once frank.toJSON'd individually for platform delivery.
  const _readSdkToken = () => {
    if (typeof window === 'undefined') return null
    try {
      return window.localStorage.getItem('symbols_access_token')
    } catch {
      return null
    }
  }
  const _decodeJwt = (token) => {
    if (!token || typeof token !== 'string') return null
    const parts = token.split('.')
    if (parts.length !== 3) return null
    try {
      const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/')
      const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
      return JSON.parse(json)
    } catch {
      return null
    }
  }
  const _expiryStatus = (epochSec) => {
    if (!epochSec) return { expired: null, secondsLeft: null }
    const now = Date.now() / 1000
    const left = epochSec - now
    return { expired: left <= 0, secondsLeft: Math.floor(left) }
  }

  const sdkToken = _readSdkToken()

  const sdkClaims = _decodeJwt(sdkToken)
  const sdkExp = _expiryStatus(sdkClaims?.exp)

  const sdk = {
    present: !!sdkToken,
    expiresInSec: sdkExp.secondsLeft,
    expired: sdkExp.expired
  }

  let diagnosis
  let suggestedAction
  if (!sdk.present) {
    diagnosis = 'all_missing'
    suggestedAction = 'No tokens in localStorage. Sign in.'
  } else {
    diagnosis = 'sdk_only'
    suggestedAction =
      'SDK token present; the legacy governance JWT is absent — which is the NORMAL state since the federation plane was removed. No action needed.'
  }

  return { sdk, diagnosis, suggestedAction }
}

/**
 * Runs `diagnoseAuth()` and prints a human-readable summary to the
 * console — exposed as a global on window so it's invokable from the
 * browser console without an import path:
 *   > __auth.diagnose()
 *
 * Wired by bootShell at boot.
 */
export const runAuthDiagnostic = () => {
  // FA204 / frank trap — self-contained inline copy of diagnoseAuth (a bare
  // sibling-export call is exactly as unresolvable post-frank.toJSON as a
  // module-local read; the export above stays live for its real consumer,
  // bootShell.js's plain ESM import — cross-file imports resolve fine via
  // frank's globalScope fallback, only bare same-file sibling reads break).
  const _diagnoseAuth = () => {
    const _readSdkToken = () => {
      if (typeof window === 'undefined') return null
      try {
        return window.localStorage.getItem('symbols_access_token')
      } catch {
        return null
      }
    }
    const _decodeJwt = (token) => {
      if (!token || typeof token !== 'string') return null
      const parts = token.split('.')
      if (parts.length !== 3) return null
      try {
        const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        const json = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
        return JSON.parse(json)
      } catch {
        return null
      }
    }
    const _expiryStatus = (epochSec) => {
      if (!epochSec) return { expired: null, secondsLeft: null }
      const now = Date.now() / 1000
      const left = epochSec - now
      return { expired: left <= 0, secondsLeft: Math.floor(left) }
    }

    const sdkToken = _readSdkToken()
    const sdkClaims = _decodeJwt(sdkToken)
    const sdkExp = _expiryStatus(sdkClaims?.exp)
    const sdk = {
      present: !!sdkToken,
      expiresInSec: sdkExp.secondsLeft,
      expired: sdkExp.expired
    }
    let diagnosis
    let suggestedAction
    if (!sdk.present) {
      diagnosis = 'all_missing'
      suggestedAction = 'No tokens in localStorage. Sign in.'
    } else {
      diagnosis = 'sdk_only'
      suggestedAction =
        'SDK token present; the legacy governance JWT is absent — which is the NORMAL state since the federation plane was removed. No action needed.'
    }
    return { sdk, diagnosis, suggestedAction }
  }
  const result = _diagnoseAuth()

  console.group('[auth-diagnostic]')

  console.log('diagnosis:', result.diagnosis)

  console.log('suggestion:', result.suggestedAction)

  console.log('sdk:', result.sdk)

  console.groupEnd()
  return result
}
